import { LAT_LIMIT, LNG_LIMIT, formatLocation } from './elementFormat/location';
import { normalizeId } from './ids';

/**
 * Where an item sits on the map (KPR-392). Pure: no React, no map library.
 *
 * A `location` element stores `{ address?, lat?, lng? }` with only the keys
 * that are set (keepr-api SCHEMA.md, "location"). The map places an item by
 * its COORDINATES only — keepr does not geocode an address (the item's
 * ruling), so an address alone is "no location" here.
 */

const LOCATION = 'location';

const isFiniteNumber = value => typeof value === 'number' && Number.isFinite(value);

/**
 * `{ lat, lng }` when a stored location value holds a usable pair, else null.
 * The server refuses half a pair and anything out of range on write; this
 * checks again because a map handed NaN or 91° throws, and data imported
 * before a rule existed is still data. `{ lat: 0, lng: 0 }` is a place (the
 * Gulf of Guinea), not an empty value.
 */
export function coordinatesOf(value) {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
	const { lat, lng } = value;
	if (!isFiniteNumber(lat) || !isFiniteNumber(lng)) return null;
	if (lat < -LAT_LIMIT || lat > LAT_LIMIT || lng < -LNG_LIMIT || lng > LNG_LIMIT) return null;
	return { lat, lng };
}

/**
 * A card's location elements, in definition order. Top-level elements only:
 * a repeating group's location column is many places per item, which a pin
 * per item cannot say (⚑ KPR-392).
 */
export function locationElementsOf(elementDefs) {
	return (Array.isArray(elementDefs) ? elementDefs : [])
		.filter(def => def && def.dataType === LOCATION && def.name);
}

/** Does any of these cards declare a location element? */
export function anyCardHasLocation(cardIds, getElementsForCard) {
	if (typeof getElementsForCard !== 'function' || !cardIds) return false;
	for (const id of cardIds) {
		if (locationElementsOf(getElementsForCard(id)).length) return true;
	}
	return false;
}

/**
 * Where ONE item goes: the first location element of its card, in definition
 * order, that holds coordinates (⚑ KPR-392: a card with two places — a site
 * and a mailing address — is placed by the first one that can be placed).
 *
 *   { lat, lng, element, address } | null
 *
 * `address` is what the value reads as (its address, or the coordinates when
 * it has none), for the list beside the map.
 */
export function itemPoint(item, elementDefs) {
	const elements = item?.elements;
	if (!elements || typeof elements !== 'object') return null;
	for (const def of locationElementsOf(elementDefs)) {
		const value = elements[def.name];
		const coords = coordinatesOf(value);
		if (coords) return { ...coords, element: def.name, address: formatLocation(value) };
	}
	return null;
}

/**
 * Split a list of items into those the map can place and those it cannot,
 * keeping the list's own order in both.
 *
 *   { placed: [{ item, id, lat, lng, element, address }], unplaced: [item] }
 *
 * An item without an id is left out of both: nothing could open it.
 */
export function placeItems(items, getElementsForCard) {
	const placed = [];
	const unplaced = [];
	const seen = new Set();
	(Array.isArray(items) ? items : []).forEach((item) => {
		const id = normalizeId(item?._id);
		if (!id || seen.has(id)) return;
		seen.add(id);
		const defs = typeof getElementsForCard === 'function' ? getElementsForCard(item?.card_id) : null;
		const point = itemPoint(item, defs);
		if (point) placed.push({ item, id, ...point });
		else unplaced.push(item);
	});
	return { placed, unplaced };
}

/**
 * The placed items as a GeoJSON FeatureCollection for the map's source. Each
 * feature carries only the item's id: titles and values stay in React, so
 * nothing about an item is handed to the map library beyond where it is.
 */
export function pointsGeoJson(placed) {
	return {
		type: 'FeatureCollection',
		features: (Array.isArray(placed) ? placed : []).map(point => ({
			type: 'Feature',
			properties: { itemId: point.id, ...(point.role ? { role: point.role } : {}) },
			geometry: { type: 'Point', coordinates: [point.lng, point.lat] },
		})),
	};
}

/**
 * The bounds that hold every placed item, `[[west, south], [east, north]]`,
 * or null when there are none.
 */
export function boundsOf(placed) {
	const list = Array.isArray(placed) ? placed : [];
	if (!list.length) return null;
	let west = Infinity; let south = Infinity; let east = -Infinity; let north = -Infinity;
	list.forEach(({ lat, lng }) => {
		if (lng < west) west = lng;
		if (lng > east) east = lng;
		if (lat < south) south = lat;
		if (lat > north) north = lat;
	});
	return [[west, south], [east, north]];
}
